import type { PollAnswerInput, PollDetailDto, PollSummaryDto } from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime, fromDateTimeLocal, relativeDays, toDateTimeLocal } from '../lib/format';
import { useConfirm } from '../components/ConfirmDialog';
import { PageHeader } from '../components/PageHeader';
import { PollAnswerForm, PollResults } from '../components/polls/PollAnswerForm';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  EmptyState,
  ErrorBlock,
  Field,
  Input,
  LoadingBlock,
  SectionTitle,
  Tabs,
  useToast,
} from '../components/ui';

/** Status einer Umfrage aus Sicht der ansehenden Person. */
export function PollStatus({ poll }: { poll: PollSummaryDto }): JSX.Element {
  if (!poll.isOpen) return <Badge tone="neutral">Beendet</Badge>;
  if (poll.isRecipient && !poll.myAnsweredAt) return <Badge tone="warning">Antwort offen</Badge>;
  if (poll.isRecipient) return <Badge tone="success">Beantwortet</Badge>;
  return <Badge tone="brand">Läuft</Badge>;
}

function PollRow({ poll }: { poll: PollSummaryDto }): JSX.Element {
  return (
    <li>
      <Link
        to={`/umfragen/${poll.id}`}
        className="flex flex-wrap items-start justify-between gap-3 rounded-lg px-2 py-3 hover:bg-slate-50"
      >
        <span className="min-w-0">
          <span className="block break-words font-medium text-slate-800">{poll.title}</span>
          <span className="block text-xs text-slate-500">
            {poll.organisationUnitName}
            {poll.createdByName && ` · von ${poll.createdByName}`}
            {poll.isOpen && poll.closesAt && ` · bis ${formatDateTime(poll.closesAt)}`}
            {poll.canEvaluate && ` · ${poll.answeredCount} von ${poll.recipientCount} geantwortet`}
          </span>
        </span>
        <span className="flex shrink-0 flex-wrap gap-1">
          {poll.anonymous && <Badge tone="info">Anonym</Badge>}
          <PollStatus poll={poll} />
        </span>
      </Link>
    </li>
  );
}

/**
 * Umfragen.
 *
 * Zwei Blickwinkel: "An mich" - worauf ich antworten soll - und
 * "Auswertung" - was ich gestartet habe oder im Bereich verwalte.
 */
export function PollsPage(): JSX.Element {
  const { can } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ['polls'],
    queryFn: () => api.get<PollSummaryDto[]>('/api/polls'),
  });
  const mine = (data ?? []).filter((poll) => poll.isRecipient);
  const evaluated = (data ?? []).filter((poll) => poll.canEvaluate);
  const pending = mine.filter((poll) => poll.isOpen && !poll.myAnsweredAt);
  const [tab, setTab] = useState<'mine' | 'evaluate'>('mine');
  const active = evaluated.length > 0 && (tab === 'evaluate' || mine.length === 0) ? 'evaluate' : 'mine';

  return (
    <div className="space-y-4">
      <PageHeader
        title="Umfragen"
        description="Abstimmungen und Rückfragen in Mannschaften, Gruppen und Sektionen"
        scoped={false}
        actions={can('polls', 'create') ? <Button onClick={() => navigate('/umfragen/neu')}>+ Umfrage</Button> : undefined}
      />

      {isLoading && <LoadingBlock />}
      {error && <ErrorBlock error={error} />}

      {data && (
        <>
          {evaluated.length > 0 && (
            <Tabs
              tabs={[
                { id: 'mine', label: 'An mich', badge: pending.length },
                { id: 'evaluate', label: 'Gestartet und Auswertung' },
              ]}
              active={active}
              onChange={(id) => setTab(id as 'mine' | 'evaluate')}
            />
          )}
          {(active === 'mine' ? mine : evaluated).length === 0 ? (
            <EmptyState
              title={active === 'mine' ? 'Keine Umfragen an dich' : 'Noch keine Umfragen gestartet'}
              description={can('polls', 'create') ? 'Mit „+ Umfrage“ fragst du eine Mannschaft, Gruppe, Sektion oder einzelne Personen.' : undefined}
            />
          ) : (
            <Card>
              <CardBody>
                <ul className="divide-y divide-slate-100">
                  {(active === 'mine' ? mine : evaluated).map((poll) => <PollRow key={poll.id} poll={poll} />)}
                </ul>
              </CardBody>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

export function PollDetailPage(): JSX.Element {
  const { id = '' } = useParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [deadlineOpen, setDeadlineOpen] = useState(false);
  const [deadline, setDeadline] = useState('');

  const { data: poll, isLoading, error } = useQuery({
    queryKey: ['poll', id],
    queryFn: () => api.get<PollDetailDto>(`/api/polls/${id}`),
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['poll', id] });
    await queryClient.invalidateQueries({ queryKey: ['polls'] });
  };
  const fail = (fallback: string) => (err: unknown) => toast.show(err instanceof ApiError ? err.message : fallback, 'error');

  const answer = useMutation({
    mutationFn: (input: PollAnswerInput) => api.put(`/api/polls/${id}/answer`, input),
    onSuccess: async () => {
      setEditing(false);
      await refresh();
      toast.show('Danke, deine Antwort ist gespeichert');
    },
    onError: fail('Antwort konnte nicht gespeichert werden'),
  });
  const remind = useMutation({
    mutationFn: () => api.post<{ reminded: number; withoutLogin: number; viaPush: number }>(`/api/polls/${id}/remind`),
    onSuccess: async (result) => {
      await refresh();
      const unreachable = result.withoutLogin - result.viaPush;
      const parts = [`${result.reminded + result.viaPush} erinnert`];
      if (unreachable > 0) parts.push(`${unreachable} ohne Zugang und ohne Push sehen die Umfrage beim nächsten Öffnen ihres persönlichen Links`);
      toast.show(parts.join(' – '));
    },
    onError: fail('Erinnern nicht möglich'),
  });
  const close = useMutation({
    mutationFn: () => api.post(`/api/polls/${id}/close`),
    onSuccess: async () => { await refresh(); toast.show('Umfrage beendet'); },
    onError: fail('Beenden nicht möglich'),
  });
  const extend = useMutation({
    mutationFn: () => api.patch(`/api/polls/${id}`, { closesAt: fromDateTimeLocal(deadline) }),
    onSuccess: async () => { setDeadlineOpen(false); await refresh(); toast.show('Frist gespeichert'); },
    onError: fail('Frist konnte nicht gespeichert werden'),
  });
  const remove = useMutation({
    mutationFn: () => api.del(`/api/polls/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['polls'] });
      toast.show('Umfrage gelöscht');
      navigate('/umfragen');
    },
    onError: fail('Löschen nicht möglich'),
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorBlock error={error} />;
  if (!poll) return <EmptyState title="Umfrage nicht gefunden" />;

  const mayAnswer = poll.isRecipient && poll.isOpen && !user?.isImpersonating;
  const showForm = mayAnswer && (!poll.myAnsweredAt || editing);
  const pendingCount = poll.recipientCount - poll.answeredCount;

  return (
    <div className="space-y-4">
      {confirmDialog}
      <Link to="/umfragen" className="text-sm font-medium text-brand-700">← Umfragen</Link>
      <PageHeader
        title={poll.title}
        description={[
          poll.organisationUnitName,
          poll.createdByName && `von ${poll.createdByName}`,
          poll.closesAt && (poll.isOpen ? `läuft bis ${formatDateTime(poll.closesAt)}` : `Frist ${formatDateTime(poll.closesAt)}`),
        ].filter(Boolean).join(' · ')}
        scoped={false}
        actions={
          <span className="flex flex-wrap gap-1">
            {poll.anonymous && <Badge tone="info">Anonym</Badge>}
            <PollStatus poll={poll} />
          </span>
        }
      />

      {poll.description && (
        <Card><CardBody><p className="whitespace-pre-wrap text-sm text-slate-700">{poll.description}</p></CardBody></Card>
      )}

      {poll.isRecipient && (
        <Card>
          <CardBody>
            <SectionTitle
              title="Deine Antwort"
              subtitle={poll.myAnsweredAt ? `Gesendet ${formatDateTime(poll.myAnsweredAt)}` : poll.isOpen ? undefined : 'Die Umfrage ist beendet.'}
            />
            {user?.isImpersonating && poll.isOpen && (
              <p className="hint">In der Testansicht kann nicht im Namen dieser Person geantwortet werden.</p>
            )}
            {showForm ? (
              <PollAnswerForm
                poll={poll}
                submitting={answer.isPending}
                onSubmit={(input) => answer.mutate(input)}
                onCancel={editing ? () => setEditing(false) : undefined}
              />
            ) : poll.myAnsweredAt ? (
              <div className="space-y-3">
                {poll.myAnswers ? (
                  <dl className="space-y-2 text-sm">
                    {poll.questions.map((question) => {
                      const own = poll.myAnswers?.[question.id];
                      const labels = question.options.filter((o) => own?.optionIds.includes(o.id)).map((o) => o.label);
                      return (
                        <div key={question.id}>
                          <dt className="text-slate-500">{question.text}</dt>
                          <dd className="whitespace-pre-wrap font-medium text-slate-800">{own?.text ?? (labels.join(', ') || '–')}</dd>
                        </div>
                      );
                    })}
                  </dl>
                ) : (
                  <p className="text-sm text-slate-600">Deine Antwort ist anonym gespeichert.</p>
                )}
                {mayAnswer && !poll.anonymous && (
                  <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>Antwort ändern</Button>
                )}
              </div>
            ) : null}
          </CardBody>
        </Card>
      )}

      {poll.canManage && (
        <Card>
          <CardBody className="space-y-3">
            <SectionTitle
              title="Rücklauf"
              subtitle={`${poll.answeredCount} von ${poll.recipientCount} haben geantwortet · ${poll.audienceLabel}`}
            />
            <div className="h-2 overflow-hidden rounded-full bg-slate-100" aria-hidden="true">
              <div
                className="h-full rounded-full bg-emerald-500"
                style={{ width: `${poll.recipientCount ? (poll.answeredCount / poll.recipientCount) * 100 : 0}%` }}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {poll.isOpen && pendingCount > 0 && (
                <Button size="sm" loading={remind.isPending} onClick={async () => {
                    const ok = await confirm({
                      title: `${pendingCount} ${pendingCount === 1 ? 'Person' : 'Personen'} eine Erinnerung schicken?`,
                      message: 'Alle, die noch nicht geantwortet haben, bekommen eine Erinnerung zur Umfrage.',
                      confirmLabel: 'Ja, erinnern',
                    });
                    if (ok) remind.mutate();
                  }}>
                  {pendingCount} erinnern
                </Button>
              )}
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setDeadline(toDateTimeLocal(poll.closesAt ?? new Date(Date.now() + 7 * 86400_000).toISOString()));
                  setDeadlineOpen(true);
                }}
              >
                {poll.isOpen ? 'Frist ändern' : 'Wieder öffnen'}
              </Button>
              {poll.isOpen && (
                <Button
                  size="sm"
                  variant="secondary"
                  loading={close.isPending}
                  onClick={() => { if (window.confirm('Umfrage jetzt beenden? Danach sind keine Antworten mehr möglich.')) close.mutate(); }}
                >
                  Beenden
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                loading={remove.isPending}
                onClick={() => { if (window.confirm(`Umfrage „${poll.title}“ mit allen Antworten löschen?`)) remove.mutate(); }}
              >
                Löschen
              </Button>
            </div>
            {poll.recipients && (
              <details className="text-sm">
                <summary className="cursor-pointer font-medium text-slate-700">Wer hat geantwortet?</summary>
                <ul className="mt-2 divide-y divide-slate-100">
                  {poll.recipients.map((recipient) => (
                    <li key={recipient.personId} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
                      <span>
                        {recipient.name}
                        {!recipient.hasLogin && <span className="ml-1 text-xs text-slate-400">(persönlicher Link)</span>}
                        {recipient.push && <Badge tone="info" className="ml-1.5" title="Erinnerungen kommen als Push-Nachricht an">Push</Badge>}
                      </span>
                      {recipient.answeredAt
                        ? <Badge tone="success">{formatDateTime(recipient.answeredAt)}</Badge>
                        : <Badge tone="neutral">offen</Badge>}
                    </li>
                  ))}
                </ul>
                {poll.anonymous && (
                  <p className="hint mt-2">Anonym: sichtbar ist nur, wer geantwortet hat – nicht was. Bei sehr wenigen Antworten kann man es trotzdem erraten.</p>
                )}
              </details>
            )}
          </CardBody>
        </Card>
      )}

      {poll.results && (
        <Card>
          <CardBody>
            <SectionTitle
              title="Ergebnis"
              subtitle={poll.canEvaluate ? undefined : 'Gesamtergebnis aller Antworten'}
            />
            <PollResults poll={poll} />
          </CardBody>
        </Card>
      )}

      {poll.canEvaluate && !poll.anonymous && poll.recipients && poll.answeredCount > 0 && (
        <Card>
          <CardBody>
            <SectionTitle title="Antworten je Person" />
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-xs text-slate-500">
                    <th className="py-2 pr-3 font-medium">Person</th>
                    {poll.questions.map((q) => <th key={q.id} className="py-2 pr-3 font-medium">{q.text}</th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {poll.recipients.filter((r) => r.answers).map((recipient) => (
                    <tr key={recipient.personId}>
                      <td className="py-2 pr-3 font-medium text-slate-800">{recipient.name}</td>
                      {poll.questions.map((q) => {
                        const own = recipient.answers?.[q.id];
                        const labels = q.options.filter((o) => own?.optionIds.includes(o.id)).map((o) => o.label);
                        return <td key={q.id} className="whitespace-pre-wrap py-2 pr-3 text-slate-700">{own?.text ?? (labels.join(', ') || '–')}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      )}

      <Dialog
        open={deadlineOpen}
        onClose={() => setDeadlineOpen(false)}
        title={poll.isOpen ? 'Frist ändern' : 'Umfrage wieder öffnen'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeadlineOpen(false)}>Abbrechen</Button>
            <Button loading={extend.isPending} disabled={!deadline} onClick={() => extend.mutate()}>Speichern</Button>
          </>
        }
      >
        <Field label="Antworten möglich bis" hint={deadline ? `Endet ${relativeDays(fromDateTimeLocal(deadline))}` : undefined}>
          {(fieldId) => <Input id={fieldId} type="datetime-local" value={deadline} onChange={(e) => setDeadline(e.target.value)} />}
        </Field>
      </Dialog>
    </div>
  );
}
